import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import { ICodSignatureStatus } from "./useCodSignature";

/**
 * The signed-in user's own CoD signature. Distinct from useCodSignature, which manages the
 * customer-wide one on the admin CoD page — this API never takes a user id, so it can only ever
 * read or change your own.
 *
 * There is no apply toggle here: whether CoDs get signed at all stays a customer-wide admin
 * setting. `applySignature` comes back read-only so the page can say when a signature won't be used.
 */
export default function useMyCodSignature() {
  const { get, del, postDocs } = useApiHelper();
  const queryClient = useQueryClient();

  const queryKey = ["profile", "codSignature"];

  const statusQuery = useQuery({
    queryKey,
    queryFn: () =>
      get<DefaultResponseWithData<ICodSignatureStatus>>("/profile/cod-signature"),
  });

  const upload = useMutation((data: FormData) => postDocs("/profile/cod-signature", data), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });

  const remove = useMutation(() => del("/profile/cod-signature"), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });

  return {
    status: statusQuery.data?.data,
    loading: statusQuery.isLoading,
    upload,
    remove,
  };
}
